import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function TermsOfService() {
  const { t } = useTranslation('terms');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-950 text-white py-6">
        <div className="max-w-4xl mx-auto px-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            {t('backToHome')}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">{t('title')}</h1>
          <p className="text-slate-400 mt-2">{t('lastUpdated')}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-slate max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.introduction.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.introduction.paragraph1')}
            </p>
            <p className="text-slate-700 leading-relaxed">
              {t('sections.introduction.paragraph2')}
            </p>
          </section>

          {/* Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.services.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.services.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.services.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              {t('sections.services.outro')}
            </p>
          </section>

          {/* Permitted Use */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.usage.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.usage.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.usage.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Prohibited Use */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.prohibited.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.prohibited.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.prohibited.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.intellectualProperty.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.intellectualProperty.paragraph1')}
            </p>
            <p className="text-slate-700 leading-relaxed">
              {t('sections.intellectualProperty.paragraph2')}
            </p>
          </section>

          {/* Disclaimer */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.disclaimer.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.disclaimer.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.disclaimer.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              {t('sections.disclaimer.outro')}
            </p>
          </section>

          {/* Liability */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.liability.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.liability.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.liability.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              {t('sections.liability.outro')}
            </p>
          </section>

          {/* Payment */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.payment.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.payment.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.payment.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Termination */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.termination.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.termination.intro')}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              {t('sections.termination.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              {t('sections.termination.outro')}
            </p>
          </section>

          {/* Applicable Law */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.law.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.law.paragraph1')}
            </p>
            <p className="text-slate-700 leading-relaxed">
              {t('sections.law.paragraph2')}
            </p>
          </section>

          {/* Data Protection */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.dataProtection.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.dataProtection.paragraph1')}
            </p>
            <p className="text-slate-700 leading-relaxed">
              {t('sections.dataProtection.paragraph2')}
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.changes.title')}</h2>
            <p className="text-slate-700 leading-relaxed">
              {t('sections.changes.content')}
            </p>
          </section>

          {/* Severability */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.severability.title')}</h2>
            <p className="text-slate-700 leading-relaxed">
              {t('sections.severability.content')}
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('sections.contact.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('sections.contact.intro')}
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-semibold text-slate-900 mb-3">{t('sections.contact.company')}</p>
              <div className="space-y-2 text-slate-700">
                <p className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-teal-500"></i>
                  <span className="font-medium">{t('sections.contact.address')}:</span> Krommestraat 48, 2830 Blaasveld, België
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-phone-line text-teal-500"></i>
                  <span className="font-medium">{t('sections.contact.phone')}:</span>
                  <a href="tel:+32488139664" className="hover:text-teal-600 transition-colors cursor-pointer">
                    +32 488 13 96 64
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-mail-line text-teal-500"></i>
                  <span className="font-medium">{t('sections.contact.email')}:</span>
                  <a href="mailto:david@novaventure.be" className="hover:text-teal-600 transition-colors cursor-pointer">
                    david@novaventure.be
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-building-line text-teal-500"></i>
                  <span className="font-medium">{t('sections.contact.businessNumber')}:</span> 1006303437
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-file-text-line text-teal-500"></i>
                  <span className="font-medium">{t('sections.contact.vatNumber')}:</span> BE1006303437
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-8 mt-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
}